import { Alert, Snackbar } from "@mui/material";
import React, { useContext, useMemo } from "react";

import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { useAccountStatus } from "@/hooks/workspace/useAccountStatus";
import { AppContext } from "@/models/AppStateProvider";
import { FeedContext } from "@/models/FeedContextProvider";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { transformWorkspaceMembershipAvailabilityRecord } from "@/utils";
import TTSForm from "../ModalForms/TTSForm";
import "./index.css";
import RecordingRequestHandler from "@/components/electron/RecordingRequestHandler.tsx";
import MakeCall from "@/components/MakeCall";
import RecordingButton from "@/components/RecordingButton";

const focusToTTSForm = () => {
  const input = document.getElementById("tts-form-text-input");
  if (input) {
    setTimeout(() => input.focus(), 50);
  }
};

export default function SessionRecorderFooter({
  workspaceId,
  feedId,
  aliasOwnerAccountId,
}: {
  workspaceId: string;
  feedId: string;
  aliasOwnerAccountId?: string;
}) {
  const { flags } = useContext(AppContext);
  const { currentFeed } = useContext(CurrentFeedContext);
  const { myAccount } = useContext(MyAccountContext);
  const { scrollToBottomOfFeed } = useContext(FeedContext);
  const accountRecordWithStatus = useAccountStatus(aliasOwnerAccountId);

  const transformedAccount = useMemo(() => {
    return accountRecordWithStatus?.id ? transformWorkspaceMembershipAvailabilityRecord(accountRecordWithStatus) : null;
  }, [accountRecordWithStatus]);

  const { allowDispatcherCalls } = flags;

  const recorder = useVoiceRecorder({
    workspaceId,
    feedId,
    isSilent: () => currentFeed?.isSilent === true,
    onStopped: focusToTTSForm,
    onFinished: scrollToBottomOfFeed,
  });
  const { recording, processing, uploadProgress, error } = recorder;

  const showMakeCallButton = useMemo(
    () => allowDispatcherCalls && aliasOwnerAccountId,
    [allowDispatcherCalls, aliasOwnerAccountId],
  );

  if (!workspaceId || !feedId) return null;

  const recordingErrorSnackbar = error ? (
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{
        width: "100%",
        px: 2,
        py: 1,
        top: { xs: 100 },
        left: { xs: 0 },
        transform: { xs: "none" },
        zIndex: 1100,
      }}
      open={true}
      autoHideDuration={6000}
      onClose={recorder.clearError}
    >
      <Alert
        severity="error"
        onClose={recorder.clearError}
        sx={{ width: "100%", maxWidth: 425 }}
        elevation={6}
        variant="filled"
      >
        {error?.message || "Failed to send recording. Please try again."}
      </Alert>
    </Snackbar>
  ) : null;

  return (
    <>
      {recordingErrorSnackbar}
      <TTSForm
        makeCallButton={
          showMakeCallButton ? (
            <MakeCall
              disabled={recording}
              feedId={feedId}
              toAccountId={myAccount.id}
              fromAccountId={aliasOwnerAccountId}
              name={accountRecordWithStatus?.name}
              handsFreeEnabled={accountRecordWithStatus?.mostRecentHandsFreeStatusEnabled}
              offline={transformedAccount?.offline}
            />
          ) : null
        }
        recordingProps={{
          progress: recorder.progress,
          processing,
          audioLevel: recorder.audioLevel,
          showAudioLevels: recorder.showAudioLevels,
          time: recorder.time,
          disabled: recorder.disabled,
          uploading: recorder.uploading,
          color: recorder.color,
          uploadProgress,
        }}
        cancelRecording={() => recorder.cancel()}
        saveRecording={() => recorder.stop()}
        makeRecording={
          <RecordingRequestHandler>
            <RecordingButton
              processing={processing}
              progress={recorder.progress}
              uploading={recorder.uploading}
              disabled={recorder.disabled}
              toggleRecord={recording ? recorder.stop : recorder.start}
            />
          </RecordingRequestHandler>
        }
        disableControls={recording}
      />
    </>
  );
}
